import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { CardsModule, IMyDate, MDBBootstrapModulesPro, MDBModalService } from 'ng-uikit-pro-standard';

import dayjs from 'dayjs';

import { User } from '../../../../../models/user.model';
import { PATHS } from 'models/bary-paths.model';
import { PositionEnum } from '../../../../../models/enums/position.enum';
import { LIST_POSITION, Position } from '../../../../../models/position.model';
import { CIVILITIES_OPTIONS } from 'models/enums/civilities-option.enum';

import { UserTypeEnum } from '../../../../../models/enums/user-type.enum';
import {
  BaryButtonComponent,
  BaryCardComponent,
  BaryInputEmailComponent,
  BaryInputPasswordComponent,
  BaryInputPhoneComponent,
  BaryInputSelectComponent,
  BaryInputTextComponent,
} from '../../../components';

import { TranslateDirective } from '../../../../../directives/translate.directive';

import { BaryDatePickerComponent } from '../../../components/bary-date-picker/bary-date-picker.component';

import { PhoneFormatPipe } from '../../../../pipes/phone.pipe';
import { ModalChangePasswordComponent } from './modal-change-password/modal-change-password.component';

@Component({
  selector: 'jhi-user-card',
  standalone: true,
  imports: [
    BaryInputTextComponent,
    BaryInputSelectComponent,
    TranslateModule,
    BaryButtonComponent,
    TranslateDirective,
    BaryCardComponent,
    BaryInputPhoneComponent,
    BaryDatePickerComponent,
    BaryInputEmailComponent,
    BaryInputPasswordComponent,
    DatePipe,
    PhoneFormatPipe,
    CardsModule,
    ReactiveFormsModule,
    CommonModule,
    MDBBootstrapModulesPro,
  ],
  templateUrl: './user-card.component.html',
})
export class UserCardComponent {
  readonly #translateService = inject(TranslateService);
  readonly #modalService = inject(MDBModalService);

  public readonly CIVILITIES_OPTIONS = CIVILITIES_OPTIONS;

  public DEFAULT_OPTIONS = {
    minDate: { year: dayjs().year() - 80, month: 1, day: 1 } as IMyDate,
    maxDate: { year: dayjs().year() - 18, month: dayjs().month() + 1, day: dayjs().date() } as IMyDate,
    disableUntilDate: { year: dayjs().year() - 80, month: 1, day: 1 } as IMyDate,
  };

  public PATHS = PATHS;

  @Input() baryForm!: FormGroup;
  @Input() description?: string;
  @Input() register: boolean = false;
  @Input() isBaryUser: boolean = true;
  @Input() touched: boolean = false;
  @Input() isEditMode: boolean = true;
  @Input() user?: User;
  @Input() switchButton = false;

  @Output() action = new EventEmitter<boolean>();

  public positionCompanyOptions = [
    LIST_POSITION[PositionEnum.COMPANY_DIRECTOR],
    LIST_POSITION[PositionEnum.COMPANY_HUMAN_RESOURCE_MANAGER],
    LIST_POSITION[PositionEnum.COMPANY_ACCOUNTANT],
    LIST_POSITION[PositionEnum.COMPANY_SALES_ADMINISTRATOR],
    LIST_POSITION[PositionEnum.COMPANY_MARKETING_MANAGER],
    LIST_POSITION[PositionEnum.COMPANY_IT_MANAGER],
    LIST_POSITION[PositionEnum.COMPANY_COMMERCIAL],
    LIST_POSITION[PositionEnum.COMPANY_CUSTOMER_SUCCESS],
    LIST_POSITION[PositionEnum.COMPANY_OPERATIONAL],
    LIST_POSITION[PositionEnum.COMPANY_FINANCE_MANAGER],
    LIST_POSITION[PositionEnum.COMPANY_LEGAL_MANAGER],
    LIST_POSITION[PositionEnum.COMPANY_COMMUNICATION_MANAGER],
    LIST_POSITION[PositionEnum.OTHER],
  ].map(val => ({ label: this.#translateService.instant(val.labelKey)!, value: val.code }));

  public positionAssociationOptions = [
    LIST_POSITION[PositionEnum.ASSOCIATION_PRESIDENT],
    LIST_POSITION[PositionEnum.ASSOCIATION_ACCOUNTANT],
    LIST_POSITION[PositionEnum.COMPANY_ACCOUNTANT],
    LIST_POSITION[PositionEnum.OTHER],
  ].map(val => ({ label: this.#translateService.instant(val.labelKey)!, value: val.code }));

  public get userForm(): FormGroup {
    return this.baryForm.get('userForm') as FormGroup;
  }

  public getControl(name: string): FormControl {
    return this.userForm.get(name) as FormControl;
  }

  public isIndividual(): boolean {
    return this.getControl('userType').value === UserTypeEnum.INDIVIDUAL;
  }

  public getPosition(): { label: string; value: PositionEnum }[] {
    return this.getControl('userType').value === UserTypeEnum.COMPANY ? this.positionCompanyOptions : this.positionAssociationOptions;
  }

  /**
   * Hack: Je n'arrive pas utiliser le CivilityPipe dans ce composant
   */
  public getCivility(): string {
    return this.CIVILITIES_OPTIONS.find(it => it.value === this.user?.civility)?.label ?? '';
  }

  public isPosition(t?: PositionEnum | Position): t is Position {
    return t instanceof Position;
  }

  public openModalChangePassword(): void {
    this.#modalService.show(ModalChangePasswordComponent, {
      animated: true,
      ignoreBackdropClick: true,
    });
  }
}
